import { US_STATES } from '@ems/clinical';
import { ChevronRight, Globe2, MapPin, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Badge, EmptyState, Input, PageHeader, QueryError, Spinner } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { useCategories, useLibraryProtocols, useLibraryStateCounts } from './api';

export function LibraryPage() {
  const counts = useLibraryStateCounts();
  const { activeMembership } = useAuth();
  const [query, setQuery] = useState('');
  const homeState = activeMembership?.organization.state_code;
  const states = US_STATES.filter((s) => !query || s.name.toLowerCase().includes(query.toLowerCase()) || s.code.toLowerCase() === query.toLowerCase());

  return (
    <div>
      <PageHeader title="Protocol library" subtitle="National model guidelines and state protocols published to the platform." />
      <Link to="/library/national" className="mb-8 flex items-center gap-3.5 border-y border-stone-200 px-1 py-4 transition-colors hover:bg-stone-100 dark:border-stone-800 dark:hover:bg-ink-900">
        <Globe2 className="size-5 text-stone-400 dark:text-stone-500" />
        <span className="flex-1">
          <span className="block font-semibold">National model guidelines</span>
          <span className="block text-sm text-stone-500">Baseline protocols available to every organization</span>
        </span>
        <ChevronRight className="size-4 shrink-0 text-stone-300 dark:text-stone-600" />
      </Link>
      <div className="relative mb-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-stone-400" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a state" className="pl-9" aria-label="Find a state" />
      </div>
      {counts.isError && <QueryError error={counts.error} />}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
        {states.map((s) => {
          const n = counts.data?.[s.code] ?? 0;
          return (
            <Link
              key={s.code}
              to={`/library/${s.code}`}
              className={cn(
                'flex items-center gap-2 rounded-md border bg-white px-3 py-2.5 text-sm transition-colors hover:border-stone-500 dark:bg-ink-900 dark:hover:border-stone-500',
                s.code === homeState ? 'border-stone-900 dark:border-stone-200' : 'border-stone-200 dark:border-stone-800',
              )}
            >
              <span className="w-7 font-mono text-xs font-medium text-stone-500">{s.code}</span>
              <span className="min-w-0 flex-1 truncate">{s.name}</span>
              {n > 0 ? <span className="font-mono text-xs font-medium tabular-nums">{n}</span> : <span className="font-mono text-xs text-stone-300 dark:text-stone-600">0</span>}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function StateLibraryPage() {
  const { state = 'national' } = useParams();
  const isNational = state === 'national';
  const stateInfo = US_STATES.find((s) => s.code === state.toUpperCase());
  const protocols = useLibraryProtocols(isNational ? undefined : state.toUpperCase());
  const categories = useCategories();

  const grouped = useMemo(() => {
    const names = new Map((categories.data ?? []).map((c) => [c.slug, c.name]));
    const map = new Map<string, NonNullable<typeof protocols.data>>();
    for (const p of protocols.data ?? []) map.set(p.category_slug, [...(map.get(p.category_slug) ?? []), p]);
    return [...map.entries()].map(([slug, items]) => ({ name: names.get(slug) ?? slug, items }));
  }, [protocols.data, categories.data]);

  return (
    <div>
      <PageHeader
        back={{ to: '/library', label: 'Library' }}
        title={isNational ? 'National model guidelines' : `${stateInfo?.name ?? state} protocols`}
        subtitle={isNational ? 'Published to all organizations' : 'State-level protocols in the platform library'}
      />
      {protocols.isPending ? (
        <Spinner />
      ) : protocols.isError ? (
        <QueryError error={protocols.error} retry={() => protocols.refetch()} />
      ) : grouped.length === 0 ? (
        <EmptyState icon={<MapPin className="size-10" />} title="No protocols published yet">
          State protocols are added by platform curators with permission from the state EMS office. Your agency can still publish its own protocols.
        </EmptyState>
      ) : (
        <div className="space-y-8">
          {grouped.map((g) => (
            <section key={g.name}>
              <h2 className="label-caps mb-2 text-stone-500 dark:text-stone-400">{g.name}</h2>
              <ul className="divide-y divide-stone-200 border-y border-stone-200 dark:divide-stone-800 dark:border-stone-800">
                {g.items.map((p) => (
                  <li key={p.id}>
                    <Link to={`/protocols/${p.id}`} className="flex items-center gap-3 px-1 py-3 transition-colors hover:bg-stone-100 dark:hover:bg-ink-900">
                      {p.code && <span className="w-12 shrink-0 font-mono text-xs font-medium text-stone-500">{p.code}</span>}
                      <span className="min-w-0 flex-1">
                        <span className="font-medium">{p.title}</span> {p.is_sample && <Badge tone="warning">Sample</Badge>}
                        {p.summary && <span className="line-clamp-1 block text-sm text-stone-500">{p.summary}</span>}
                      </span>
                      <ChevronRight className="size-4 shrink-0 text-stone-300 dark:text-stone-600" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
